import { MedicalImagesType, SPINESIM_MODALITIES } from "../enums/MedicalImagesEnum";
import { PLANNING_STATES } from "../enums/PlanningStateEnum";
import { generateCacheKey } from "./RemoteRenderingUtils";
import {
  MODALITY_ALLOWED_VIEWS,
  VALIDATION_VIEW_TYPES,
  CHECK_REQUIRED_VIEW_TYPES,
} from "../constants/checkConstants";

/**
 * Get the closed views set for a specific modality
 * @param {string} modality - The modality type
 * @param {Object} closedStates - Object containing all closed state sets
 * @returns {Set} The set of closed views for the modality
 */
export const getClosedViewsForModality = (modality, closedStates) => {
  const {
    closedDxViews,
    closedCtViews,
    closedMrViews,
    closedCtVolumeViews,
    closedMrVolumeViews,
  } = closedStates;

  switch (modality) {
    case SPINESIM_MODALITIES.DX:
      return closedDxViews;
    case SPINESIM_MODALITIES.CT:
      return closedCtViews;
    case SPINESIM_MODALITIES.MR:
      return closedMrViews;
    case SPINESIM_MODALITIES.CT_VOLUME:
      return closedCtVolumeViews;
    case SPINESIM_MODALITIES.MR_VOLUME:
      return closedMrVolumeViews;
    default:
      return new Set();
  }
};

/**
 * Filter views by removing closed ones
 * @param {Array} views - Array of view objects
 * @param {Set} closedViews - Set of closed view IDs
 * @returns {Array} Filtered views
 */
export const filterClosedViews = (views, closedViews) => {
  return views.filter(
    view =>
      !closedViews.has(
        view.viewData?.viewId || view.instance?._id || view._id
      )
  );
};

/**
 * Check if a specific view is in the process of closing
 * @param {string} viewId - The view ID
 * @param {string} instanceId - The instance ID
 * @param {number} index - The view index
 * @param {string} viewType - The view type/modality
 * @param {Set} closingViews - Set of views currently closing
 * @returns {boolean} True if view is closing
 */
export const isViewClosingCheck = (viewId, instanceId, index, viewType, closingViews) => {
  const viewKey = viewId || instanceId || `index-${index}-${viewType}`;
  return closingViews.has(viewKey);
};

/**
 * Organize surgery result views by modality and store in cache
 * @param {Array} surgeryResult - Array of view objects from LoadSpinesimViewStage
 * @param {Object} instances - Digital twin instances
 * @param {Array} allInstances - All available instances
 * @param {string} twinId - Digital twin ID
 * @param {Object} digitalTwinSurgeryCache - Cache object
 */
export const organizeSurgeryResultsByModality = (
  surgeryResult,
  instances,
  allInstances,
  twinId,
  digitalTwinSurgeryCache
) => {
  const sagittalInstanceId =
    instances.sagittal_dx_instance_id?._id ||
    instances.sagittal_dx_instance_id;
  const coronalInstanceId =
    instances.coronal_dx_instance_id?._id ||
    instances.coronal_dx_instance_id;
  const ctInstanceId =
    instances.ct_instance_id?._id || instances.ct_instance_id;
  const mriInstanceId =
    instances.mri_instance_id?._id || instances.mri_instance_id;

  const dxViews = surgeryResult.filter(
    view =>
      view.viewName === MedicalImagesType.LATERALDX ||
      view.viewName === MedicalImagesType.FRONTDX
  );
  const ctViews = surgeryResult.filter(
    view =>
      view.viewName === MedicalImagesType.SAGITTAL_CT ||
      view.viewName === MedicalImagesType.CORONAL_CT ||
      view.viewName === MedicalImagesType.AXIAL_CT ||
      view.viewName === MedicalImagesType.VOLUME_CT ||
      view.viewName === MedicalImagesType.SAGITTAL_DRR_CT ||
      view.viewName === MedicalImagesType.CORONAL_DRR_CT ||
      view.viewName === MedicalImagesType.AXIAL_RADIODENSITY_CT
  );
  const mriViews = surgeryResult.filter(
    view =>
      view.viewName === MedicalImagesType.SAGITTAL_MRI ||
      view.viewName === MedicalImagesType.CORONAL_MRI ||
      view.viewName === MedicalImagesType.AXIAL_MRI ||
      view.viewName === MedicalImagesType.VOLUME_MRI
  );

  if (dxViews.length > 0) {
    const sagittalInstance = allInstances.find(
      inst => inst._id === sagittalInstanceId
    );
    const coronalInstance = allInstances.find(
      inst => inst._id === coronalInstanceId
    );

    if (sagittalInstance) {
      const sagittalCacheKey = generateCacheKey(
        sagittalInstance,
        twinId,
        PLANNING_STATES.CHECK
      );
      const sagittalViews = dxViews.filter(view =>
        view.viewName.includes("Lateral")
      );

      if (sagittalViews.length > 0) {
        digitalTwinSurgeryCache.setView(
          sagittalCacheKey,
          sagittalViews
        );
      }
    }

    if (coronalInstance) {
      const coronalCacheKey = generateCacheKey(
        coronalInstance,
        twinId,
        PLANNING_STATES.CHECK
      );
      const coronalViews = dxViews.filter(view =>
        view.viewName.includes("Front")
      );

      if (coronalViews.length > 0) {
        digitalTwinSurgeryCache.setView(coronalCacheKey, coronalViews);
      }
    }
  }

  if (ctViews.length > 0) {
    const ctInstance = allInstances.find(
      inst => inst._id === ctInstanceId
    );

    if (ctInstance) {
      const ctCacheKey = generateCacheKey(
        ctInstance,
        twinId,
        PLANNING_STATES.CHECK
      );
      digitalTwinSurgeryCache.setView(ctCacheKey, ctViews);
    }
  }

  if (mriViews.length > 0) {
    const mriInstance = allInstances.find(
      inst => inst._id === mriInstanceId
    );
    if (mriInstance) {
      const mriCacheKey = generateCacheKey(
        mriInstance,
        twinId,
        PLANNING_STATES.CHECK
      );
      digitalTwinSurgeryCache.setView(mriCacheKey, mriViews);
    }
  }
};

/**
 * Determine if Check views should be refreshed based on cache state
 * @param {Array} allInstances - All available instances
 * @param {string} twinId - Digital twin ID
 * @param {Object} digitalTwinSurgeryCache - Cache object
 * @returns {boolean} True if views should be refreshed
 */
export const shouldRefreshCheckViews = (
  allInstances,
  twinId,
  digitalTwinSurgeryCache
) => {
  const shouldRefreshData = allInstances.some(instance => {
    const cacheKey = generateCacheKey(
      instance,
      twinId,
      PLANNING_STATES.VALIDATE
    );
    if (!digitalTwinSurgeryCache.hasView(cacheKey)) return false;

    const cachedData = digitalTwinSurgeryCache.getView(cacheKey);
    const views = Array.isArray(cachedData) ? cachedData : [cachedData];

    const hasValidationViews = views.some(view =>
      VALIDATION_VIEW_TYPES.includes(view.viewName)
    );

    if (hasValidationViews) {
      digitalTwinSurgeryCache.removeView(cacheKey);
    }

    return hasValidationViews;
  });

  return shouldRefreshData;
};

/**
 * Check if correct Check views exist in cache
 * @param {Array} allInstances - All available instances
 * @param {string} twinId - Digital twin ID
 * @param {Object} digitalTwinSurgeryCache - Cache object
 * @returns {boolean} True if correct Check views exist
 */
export const hasCorrectCheckViews = (
  allInstances,
  twinId,
  digitalTwinSurgeryCache
) => {
  return allInstances.some(instance => {
    const cacheKey = generateCacheKey(
      instance,
      twinId,
      PLANNING_STATES.CHECK
    );
    if (!digitalTwinSurgeryCache.hasView(cacheKey)) return false;

    const cachedData = digitalTwinSurgeryCache.getView(cacheKey);
    const views = Array.isArray(cachedData) ? cachedData : [cachedData];
    return views.some(view => CHECK_REQUIRED_VIEW_TYPES.includes(view.viewName));
  });
};

/**
 * Get current modality views from cache
 * @param {Object} params - Parameters object
 * @returns {Array} Array of view objects with metadata
 */
export const getCurrentModalityViews = ({
  selectedTwin,
  selectedModality,
  areViewsReady,
  groupedThumbnailsStandard,
  twinId,
  digitalTwinSurgeryCache,
  checkSessionVersion,
}) => {
  if (!selectedTwin || !selectedModality || !areViewsReady) {
    return [];
  }

  const views = [];
  const modalityData = groupedThumbnailsStandard.find(
    item => item.modality === selectedModality
  );

  if (!modalityData) {
    return [];
  }

  const allowedViews = MODALITY_ALLOWED_VIEWS[selectedModality] || [];

  modalityData.instances.forEach(instance => {
    const cacheKey = generateCacheKey(
      instance,
      twinId,
      PLANNING_STATES.CHECK
    );
    if (!digitalTwinSurgeryCache.hasView(cacheKey)) {
      return;
    }

    const cachedData = digitalTwinSurgeryCache.getView(cacheKey);

    if (Array.isArray(cachedData)) {
      const filteredViews = cachedData.filter(view => {
        const isAllowed = allowedViews.includes(view.viewName);
        return isAllowed;
      });

      filteredViews.forEach((view, index) => {
        views.push({
          key: `${cacheKey}_${view.viewName || index}_${checkSessionVersion}`,
          viewData: view,
          instance,
          viewName: view.viewName,
          index,
        });
      });
    } else {
      const isAllowed = allowedViews.includes(cachedData.viewName);
      if (isAllowed) {
        views.push({
          key: `${cacheKey}_${checkSessionVersion}`,
          viewData: cachedData,
          instance,
          viewName: cachedData.viewName,
          index: 0,
        });
      }
    }
  });

  return views;
};
